import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('apex_admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('apex_admin_token');
      localStorage.removeItem('apex_admin_user');
      if (window.location.pathname.startsWith('/admin')) {
        window.location.href = '/admin/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  me: () => api.get('/auth/me'),
  changePassword: (currentPassword, newPassword) => api.put('/auth/change-password', { current_password: currentPassword, new_password: newPassword }),
};

// Categories
export const categoriesAPI = {
  getAll: () => api.get('/categories'),
  getBySlug: (slug) => api.get(`/categories/${slug}`),
  create: (data) => api.post('/categories', data),
  update: (id, data) => api.put(`/categories/${id}`, data),
  delete: (id) => api.delete(`/categories/${id}`),
};

// Posts
export const postsAPI = {
  getAll: (params) => api.get('/posts', { params }),
  getFeatured: (limit = 3) => api.get('/posts/featured', { params: { limit } }),
  getRecent: (limit = 6) => api.get('/posts/recent', { params: { limit } }),
  getByCategory: (slug, page = 1) => api.get(`/posts/by-category/${slug}`, { params: { page } }),
  getBySlug: (slug) => api.get(`/posts/${slug}`),
  getForEdit: (id) => api.get(`/posts/edit/${id}`),
  create: (data) => api.post('/posts', data),
  update: (id, data) => api.put(`/posts/${id}`, data),
  delete: (id) => api.delete(`/posts/${id}`),
};

// Leads
export const leadsAPI = {
  create: (data) => api.post('/leads', data),
  getAll: () => api.get('/leads'),
  delete: (id) => api.delete(`/leads/${id}`),
};

// Tax Calculator
export const calculatorAPI = {
  getMunicipalities: () => api.get('/calculator/municipalities'),
  submit: (data) => api.post('/calculator/submit', data),
  getLeads: () => api.get('/calculator/leads'),
  deleteLead: (id) => api.delete(`/calculator/leads/${id}`),
};

// Media
export const mediaAPI = {
  upload: (file, altText = '') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('alt_text', altText);
    return api.post('/media/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getAll: () => api.get('/media'),
  getUrl: (id) => `/api/media/${id}`,
  update: (id, altText) => api.put(`/media/${id}`, null, { params: { alt_text: altText } }),
  delete: (id) => api.delete(`/media/${id}`),
  reprocessAll: () => api.post('/media/reprocess-all', {}, { timeout: 300000 }),
};

// Site Images (for admin to manage site-wide images)
export const siteImagesAPI = {
  getAll: () => api.get('/site-images'),
  get: (key) => api.get(`/site-images/${key}`),
  update: (key, data) => api.put(`/site-images/${key}`, data),
  updateSettings: (key, data) => api.patch(`/site-images/${key}/settings`, data),
  delete: (key) => api.delete(`/site-images/${key}`),
  init: () => api.post('/site-images/init'),
};

// Services Content (editable service descriptions)
export const servicesAPI = {
  getAll: () => api.get('/services'),
  get: (slug) => api.get(`/services/${slug}`),
  update: (slug, data) => api.put(`/services/${slug}`, data),
  init: () => api.post('/services/init'),
  getPosts: (slug, page = 1) => api.get(`/services/${slug}/posts`, { params: { page } }),
};

// Site Content (editable texts)
export const siteContentAPI = {
  getAll: () => api.get('/site-content'),
  update: (key, value) => api.put(`/site-content/${key}`, { value }),
  init: () => api.post('/site-content/init'),
};

export const materiaisAPI = {
  registrarDownload: (slug) => api.post(`/materiais/download/${slug}`),
  downloads: () => api.get('/materiais/downloads'),
};

// Pesquisa de valores de plantão
export const pesquisaAPI = {
  stats: () => api.get('/pesquisa-plantao/stats'),
  criar: (data) => api.post('/pesquisa-plantao/respostas', data),
  listar: () => api.get('/pesquisa-plantao/respostas'),
  atualizar: (id, data) => api.put(`/pesquisa-plantao/respostas/${id}`, data),
  excluir: (id) => api.delete(`/pesquisa-plantao/respostas/${id}`),
  exportar: (arquivo) => api.get(`/pesquisa-plantao/${arquivo}`, { responseType: 'blob' }),
};

// Stats
export const statsAPI = {
  get: () => api.get('/stats'),
};

// Seed
export const seedAPI = {
  seed: () => api.post('/seed'),
};

// Health check
export const healthAPI = {
  check: () => api.get('/health'),
};

/**
 * Normalizes media URLs to always be relative paths.
 * Converts absolute URLs like "https://healthcare-portal-br.preview.emergentagent.com/api/media/id" to "/api/media/id"
 */
export function normalizeMediaUrl(url) {
  if (!url) return url;
  const match = url.match(/\/api\/media\/[a-f0-9-]+/);
  if (match) return match[0];
  return url;
}

export default api;
